import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Link,
  Drawer,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import ApartmentIcon from '@mui/icons-material/Apartment';
import useResponsive from '../../hooks/useResponsive';
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import NavSection from 'src/NavSection';
import navConfig from './NavConfig';
import deployNavConfig from './DeployNavConfig';
import navConfigInd from './NavConfigIND';
import { alpha } from '@mui/material/styles';
import Iconify from '../../components/Iconify';

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 88;
const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
  },
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.25, 1.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.75,
  backgroundColor: alpha(theme.palette.background.neutral, 0.9),
  border: `1px solid ${theme.palette.divider}`,
  backdropFilter: 'blur(6px)',
  transition: 'all 0.3s ease',
}));

export default function DashboardSidebar({ isCollapsed, onToggle, userType, userData }) {
  const theme = useTheme();
  const company_logo = localStorage.getItem('logo');
  const ID = localStorage.getItem('ID');
  const clientId = localStorage.getItem('client_id');
  const showPoweredByBranding = String(clientId) === '1433';
  const clientLogoScale = String(clientId) === '1433' ? 1.8 : 1;
  //const image_base_path = 'https://vdointel-ai-storage.s3.us-west-2.amazonaws.com/uploads/client_logos/';
  //const logo = (client_id === "1302") ? `${image_base_path}redcar.jpg`: (client_id === "1316") ? `${image_base_path}timberland_logo.png` : (client_id === "1320") ? `${image_base_path}groves_logo.png` : company_logo;
  const logo = company_logo;

  const [user, setUser] = useState({ displayName: '', photoURL: null, displayLatter: '' });
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const displayName = localStorage.getItem('displayname');
    const logo_url = localStorage.getItem('logo_url');
    const displayLatter = displayName ? displayName.charAt(0).toUpperCase() : '';
    const photoURL = logo_url && logo_url !== 'null' ? logo_url : null;
    setUser({ displayName, displayLatter, photoURL });
  }, []);

  const navItems = ((Number(userType) === 9) || (Number(userType) === 8) || (Number(userType) === 40))
    ? deployNavConfig
    : Number(userData.IsAgentBased) === 0
      ? navConfigInd
      : navConfig;

  const isDark = theme.palette.mode === 'dark';

  const renderContent = (
    <Box
      sx={{
        height: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ height: 56, px: { xs: 1.5, sm: 2 }, py: isCollapsed ? 1 : 0.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isCollapsed ? (
          <Logo trimPadding sx={{ width: '100%', maxWidth: 56, height: 'auto' }} />
        ) : showPoweredByBranding ? (
          <Box sx={{ width: '100%', height: '100%', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                fontWeight: 500,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              Powered by
            </Typography>
            <Logo trimPadding sx={{ width: '100%', height: 'auto', maxHeight: '100%', minWidth: 0, maxWidth: 156, flex: 1 }} />
          </Box>
        ) : (
          <Logo trimPadding sx={{ width: '100%', maxWidth: 188, height: 'auto', maxHeight: '100%' }} />
        )}
      </Box>

      <Scrollbar
        sx={{
          flexGrow: 1,
          minHeight: 0,
          '& .simplebar-content': {
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
      <Box sx={{ mb: 2, mt: 2, px: isCollapsed ? 0 : 2.5, display: 'flex', justifyContent: 'center' }}>
        {isCollapsed ? (
          <Tooltip title={user.displayName || ''} placement="right">
            <Avatar
              sx={{
                bgcolor: '#ffffff',
                color: 'primary.main',
                width: 56,
                height: 56,
                borderRadius: '50%',
                overflow: 'hidden',
              }}
            >
              {logo && !imgError ? (
                <img
                  src={logo}
                  alt="Client Avatar"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    borderRadius: '50%',
                    transform: `scale(${clientLogoScale})`,
                  }}
                  onError={() => setImgError(true)}
                />
              ) : (
                <PersonIcon />
              )}
            </Avatar>
          </Tooltip>
        ) : (
          <AccountStyle sx={{ width: '100%' }}>
            <Avatar
              sx={{
                bgcolor: '#ffffff',
                color: 'primary.main',
                width: 56,
                height: 56,
                borderRadius: '50%',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {logo && !imgError ? (
                <img
                  src={logo}
                  alt="Client Avatar"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    borderRadius: '50%',
                    transform: `scale(${clientLogoScale})`,
                  }}
                  onError={() => setImgError(true)}
                />
              ) : (
                <PersonIcon />
              )}
            </Avatar>
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" sx={{ textTransform: ID !== "1302" ? "capitalize" : undefined, fontSize: '0.8rem' }}>
                {user.displayName}
              </Typography>
            </Box>
          </AccountStyle>
        )}
      </Box>

      <NavSection navConfig={navItems} userData={userData} isCollapsed={isCollapsed} />

      <Box sx={{ flexGrow: 1 }} />

      </Scrollbar>
    </Box>
  );

  return (
    <RootStyle>
      <Drawer
        open
        variant="persistent"
        PaperProps={{
          sx: {
            width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
            bgcolor: isDark ? 'background.paper' : 'grey.100',
            color: 'text.primary',
            borderRight: `1.5px dashed ${isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)'}`,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
            overflow: 'visible',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            position: 'relative',
          },
        }}
      >
        {/* Floating toggle button at right edge */}
        <Tooltip title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton
            onClick={onToggle}
            size="small"
            sx={{
              position: 'absolute',
              top: 28,
              right: -13,
              transform: 'translateY(-50%)',
              zIndex: 10,
              width: 26,
              height: 26,
              bgcolor: 'background.paper',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              color: 'primary.main',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Iconify
              icon={isCollapsed ? 'eva:arrow-ios-forward-fill' : 'eva:arrow-ios-back-fill'}
              sx={{ width: 15, height: 15 }}
            />
          </IconButton>
        </Tooltip>

        {renderContent}
      </Drawer>
    </RootStyle>
  );
}

DashboardSidebar.propTypes = {
  isCollapsed: PropTypes.bool,
  onToggle: PropTypes.func,
  userType: PropTypes.any,
  userData: PropTypes.object,
};
